import React, { useState } from 'react';

import { formatMoney } from '@betterment/core';
import { Box, Icon, Separator, Sheet, Text, TextColorToken, VisuallyHidden } from '@betterment/samba-react';
import { toTitleCase } from '@betterment/shared-components';
import { getLatestStatus } from '@betterment/shared-components/src/components/activity/utils/statusUtils';

import { trackClick } from '../../features/track';
import {
  ActivityItemFragment,
  ActivityItem_ActivityItemTradeGroup_Fragment as ActivityItemTradeGroupFragment,
} from '../../graphql/fragments/activityItem.generated';
import { ActivityItemStage, ActivityItemTradeAttribution, ActivityItemTransferGroup } from '../../graphql/schema';
import { NarrowTypename } from '../../graphql/utils/narrow-typename';
import { resolveAccountName } from '../utils/accountNameUtils';

import { Drawer } from '.';

const statusColorMap: Record<ActivityItemStage, TextColorToken> = {
  INITIATED: 'text.weak',
  FAILED: 'text.critical',
  SENT: 'text.weak',
  RETURNED: 'text.weak',
  COMPLETED: 'text.success',
  CANCELED: 'text.critical',
};

interface RowProps {
  item: ActivityItemFragment;
}

const ACTIVITY_TRADING_DETAILS_COPY_MAP: Record<ActivityItemTradeAttribution, string> = {
  [ActivityItemTradeAttribution.Rebalance]: 'Includes rebalancing',
  [ActivityItemTradeAttribution.TaxLossHarvest]: 'Includes tax-loss harvesting',
} as const;

const ACTIVITY_ITEM_TYPE_MAP: Record<ActivityItemFragment['__typename'], string> = {
  ActivityItemAllocationChange: 'portfolio changes',
  ActivityItemDeposit: 'deposits',
  ActivityItemDividendGroup: 'dividends',
  ActivityItemFee: 'fees',
  ActivityItemOther: 'other',
  ActivityItemRollover: 'rollovers',
  ActivityItemTradeGroup: 'trading',
  ActivityItemTransferGroup: 'transfers',
  ActivityItemWithdrawal: 'withdrawals',
  ActivityItemOrder: 'orders',
} as const;

function activityTradingDetails(item: ActivityItemTradeGroupFragment) {
  const { sales, purchases } = item;
  const attributions = new Set([...sales, ...purchases].map(trade => trade.tradeAttribution).filter(Boolean));

  if (attributions.has(ActivityItemTradeAttribution.TaxLossHarvest)) return ActivityItemTradeAttribution.TaxLossHarvest;
  if (attributions.has(ActivityItemTradeAttribution.Rebalance)) return ActivityItemTradeAttribution.Rebalance;
  return null;
}

function getActivityValueSubCopy(item: ActivityItemFragment): string | null {
  switch (item.__typename) {
    case 'ActivityItemTradeGroup': {
      const details = activityTradingDetails(item);
      return details && ACTIVITY_TRADING_DETAILS_COPY_MAP[details];
    }
    case 'ActivityItemOther':
    case 'ActivityItemDeposit':
    case 'ActivityItemWithdrawal':
    case 'ActivityItemFee':
    case 'ActivityItemTransferGroup':
    case 'ActivityItemRollover':
    case 'ActivityItemAllocationChange':
    case 'ActivityItemOrder':
    case 'ActivityItemDividendGroup': {
      const latestStatus = getLatestStatus(item.statuses);
      const stage = latestStatus?.stage;

      if (!stage || [ActivityItemStage.Completed, ActivityItemStage.Sent].includes(stage)) {
        return null;
      }

      return stage === ActivityItemStage.Initiated ? 'Pending' : toTitleCase(stage);
    }

    default: {
      const exhaustiveCheck: never = item;
      return exhaustiveCheck;
    }
  }
}

function getActivityValue(item: ActivityItemFragment): string {
  switch (item.__typename) {
    case 'ActivityItemOther':
    case 'ActivityItemDeposit':
    case 'ActivityItemFee':
      return item.amount ? formatMoney(item.amount) : '—';
    case 'ActivityItemWithdrawal': {
      if (item.amount !== null) {
        return item.amount ? formatMoney(item.amount) : '—';
      }
      if (item.isFullWithdrawal) {
        return 'Full withdrawal';
      }
      return '—';
    }

    case 'ActivityItemTransferGroup': {
      const transferItem = item as ActivityItemTransferGroup;

      if (transferItem.amount !== null) {
        return transferItem.amount ? formatMoney(transferItem.amount) : '—';
      }
      if (transferItem.isFullTransfer) {
        return 'Full transfer';
      }
      return '—';
    }

    case 'ActivityItemTradeGroup': {
      const tradeCount = item.sales.length + item.purchases.length;
      return `${tradeCount} trade${tradeCount === 1 ? '' : 's'}`;
    }

    case 'ActivityItemRollover':
    case 'ActivityItemAllocationChange':
    case 'ActivityItemDividendGroup': {
      type DividendType = NarrowTypename<ActivityItemFragment, 'ActivityItemDividendGroup'>;
      const dividendItem = item as DividendType;
      if (dividendItem.dividendAmount) {
        return formatMoney(dividendItem.dividendAmount);
      }
      return '—';
    }

    case 'ActivityItemOrder': {
      const amount = item.amountTraded || item.amountRequested;
      const shares = item.sharesTraded || item.sharesRequested;
      if (amount) {
        return formatMoney(amount);
      }
      if (shares) {
        return `${shares} shares`;
      }
      return '—';
    }

    default: {
      const exhaustiveCheck: never = item;
      return exhaustiveCheck;
    }
  }
}

function getStatusStage(item: ActivityItemFragment): ActivityItemStage | undefined {
  const latestStatus = getLatestStatus(item.statuses);
  const stage = latestStatus?.stage;

  return stage && ![ActivityItemStage.Completed, ActivityItemStage.Sent].includes(stage) ? stage : undefined;
}

function getStatusColor(item: ActivityItemFragment): TextColorToken {
  const stage = getStatusStage(item);
  return stage ? statusColorMap[stage] : 'text.weak';
}

function isStrikethroughStatus(stage?: ActivityItemStage): boolean {
  return !!stage && [ActivityItemStage.Canceled, ActivityItemStage.Returned].includes(stage);
}

export const Row: React.FC<RowProps> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);

  const transactionMemo = 'transactionMemo' in item ? item.transactionMemo : undefined;
  const subCopy = getActivityValueSubCopy(item);
  const statusStage = getStatusStage(item);
  const statusColor = getStatusColor(item);
  const isStrikethrough = isStrikethroughStatus(statusStage);

  const dateFormat = { month: 'short', day: 'numeric', year: 'numeric' } as const;
  const timestamp = new Date(item.timestamp);
  const date = timestamp.toLocaleDateString('en-US', dateFormat);

  const { subAccount, description } = item;
  const purposeName = subAccount.envelope.purpose?.name;
  const accountName = resolveAccountName(subAccount);

  const trackActivityDrawer = () =>
    trackClick({
      name: 'ActivityDrawer',
      activityItemStatus: statusStage,
      activityItemType: ACTIVITY_ITEM_TYPE_MAP[item.__typename],
      activityTradingDetails: item.__typename === 'ActivityItemTradeGroup' ? activityTradingDetails(item) : undefined,
    });

  return (
    <Sheet overlay="weak" open={isOpen} onOpenChange={setIsOpen}>
      <Sheet.Trigger asChild={true}>
        <Box onClick={trackActivityDrawer}>
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <Box
            data-testid="activity-row"
            css={{
              px: 'space.300',
              py: 'space.200',
              display: 'grid',
              gap: 'space.200',
              gridTemplateAreas: `"timestamp goal description value"`,
              gridTemplateColumns: '1fr 1fr 2fr 1fr',
              alignItems: 'center',
              transition: 'background-color 0.2s ease-in-out',
              backgroundColor: isOpen ? 'background.info' : undefined,
              '&:hover': {
                backgroundColor: 'background.info',
                cursor: 'pointer',
              },
            }}
          >
            <Box css={{ gridArea: 'timestamp', display: 'flex', flexDirection: 'column' }}>
              <Text>{date}</Text>
            </Box>

            <Box css={{ gridArea: 'goal', display: 'flex', flexDirection: 'column' }}>
              {purposeName ? (
                <>
                  <Text>{purposeName}</Text>
                  <Text variant="text50" color="text.weak">
                    {accountName}
                  </Text>
                </>
              ) : (
                <Text>{accountName}</Text>
              )}
            </Box>

            <Box css={{ gridArea: 'description', display: 'flex', flexDirection: 'column' }}>
              <Text>{description}</Text>
              {transactionMemo?.reason && (
                <Text variant="text50" color="text.weak">
                  {transactionMemo.reason}
                </Text>
              )}
            </Box>

            <Box
              css={{
                gridArea: 'value',
                textAlign: 'right',
                justifyContent: 'end',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 'space.100',
              }}
            >
              <Box css={{ display: 'flex', flexDirection: 'column' }}>
                <Text css={isStrikethrough ? { textDecoration: 'line-through' } : undefined}>
                  {getActivityValue(item)}
                </Text>

                {subCopy && (
                  <Text variant="text50" color={statusColor}>
                    {subCopy}
                  </Text>
                )}
              </Box>
              <Icon size="small" name="CaretRight" />
            </Box>
          </Box>
        </Box>
      </Sheet.Trigger>

      <Sheet.Content
        css={{ overflowY: 'auto', gap: 'space.300', zIndex: '300' }}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <VisuallyHidden asChild={true}>
          <Sheet.Description>{description}</Sheet.Description>
        </VisuallyHidden>
        <Drawer item={item} />
      </Sheet.Content>
    </Sheet>
  );
};
